import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["labelsInput", "delimiterSelect", "tableBody", "pasteArea", "segmentsField", "tableContainer", "pathsHint", "importButton"]
  static values = { segments: Object }

  connect() {
    if (this.segmentsValue && this.segmentsValue.labels) {
      this.labelsInputTarget.value = this.segmentsValue.labels.join(", ")
      if (this.segmentsValue.delimiter) {
        this.delimiterSelectTarget.value = this.segmentsValue.delimiter
      }
      this.renderTable()
      this.segmentsValue.paths.forEach(path => this.addPathRow(path))
    }
  }

  labelsPasted(event) {
    const pasted = (event.clipboardData || window.clipboardData).getData("text")
    if (pasted.includes("\t")) {
      event.preventDefault()
      const converted = pasted.split("\t").map(s => s.trim()).filter(s => s !== "").join(", ")
      this.labelsInputTarget.value = converted
      this.labelsChanged()
    }
  }

  labelsChanged() {
    this.renderTable()
    // Re-add existing rows adapted to new column count
    const existingRows = this.tableBodyTarget.querySelectorAll("tr")
    if (existingRows.length === 0) {
      this.addRow()
    }
  }

  renderTable() {
    const labels = this.getLabels()
    const hasLabels = labels.length > 0

    if (this.hasPathsHintTarget) {
      this.pathsHintTarget.classList.toggle("hide", hasLabels)
    }
    this.pasteAreaTarget.disabled = !hasLabels
    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = !hasLabels
    }

    if (!hasLabels) {
      this.tableContainerTarget.classList.add("hide")
      return
    }
    this.tableContainerTarget.classList.remove("hide")

    // Update thead
    const thead = this.tableContainerTarget.querySelector("thead tr")
    thead.innerHTML = ""
    labels.forEach(label => {
      const th = document.createElement("th")
      th.textContent = label
      thead.appendChild(th)
    })
    const actionsTh = document.createElement("th")
    actionsTh.textContent = ""
    actionsTh.style.width = "120px"
    thead.appendChild(actionsTh)

    // Adjust existing row cells to match column count
    this.tableBodyTarget.querySelectorAll("tr").forEach(row => {
      const cells = row.querySelectorAll("td.segment-cell")
      const diff = labels.length - cells.length
      if (diff > 0) {
        const actionsTd = row.querySelector("td.actions-cell")
        for (let i = 0; i < diff; i++) {
          const td = document.createElement("td")
          td.className = "segment-cell"
          td.innerHTML = '<input type="text" class="form-control input-sm" data-action="input->segment-admin#cellChanged">'
          row.insertBefore(td, actionsTd)
        }
      } else if (diff < 0) {
        for (let i = 0; i < Math.abs(diff); i++) {
          const lastCell = row.querySelectorAll("td.segment-cell")
          if (lastCell.length > 0) lastCell[lastCell.length - 1].remove()
        }
      }
      this.enforceContiguous(row)
    })
  }

  addRow() {
    const labels = this.getLabels()
    if (labels.length === 0) return
    this.addPathRow([])
  }

  addPathRow(values = []) {
    const labels = this.getLabels()
    const row = document.createElement("tr")

    labels.forEach((_, i) => {
      const td = document.createElement("td")
      td.className = "segment-cell"
      const input = document.createElement("input")
      input.type = "text"
      input.className = "form-control input-sm"
      input.value = values[i] || ""
      input.dataset.action = "input->segment-admin#cellChanged"
      td.appendChild(input)
      row.appendChild(td)
    })

    const moveUpLabel = window.I18n["custom_field_definitions.segment_config.move_up"]
    const moveDownLabel = window.I18n["custom_field_definitions.segment_config.move_down"]
    const removeLabel = window.I18n["custom_field_definitions.segment_config.remove"]

    const actionsTd = document.createElement("td")
    actionsTd.className = "actions-cell"
    actionsTd.innerHTML = `
      <button type="button" class="btn btn-xs btn-default" data-action="click->segment-admin#moveRowUp" title="${moveUpLabel}">&#9650;</button>
      <button type="button" class="btn btn-xs btn-default" data-action="click->segment-admin#moveRowDown" title="${moveDownLabel}">&#9660;</button>
      <button type="button" class="btn btn-xs btn-danger" data-action="click->segment-admin#removeRow" title="${removeLabel}">&times;</button>
    `
    row.appendChild(actionsTd)

    this.tableBodyTarget.appendChild(row)
    this.enforceContiguous(row)
  }

  removeRow(event) {
    const row = event.target.closest("tr")
    if (this.tableBodyTarget.querySelectorAll("tr").length > 1) {
      row.remove()
    }
  }

  moveRowUp(event) {
    const row = event.target.closest("tr")
    const prev = row.previousElementSibling
    if (prev) {
      this.tableBodyTarget.insertBefore(row, prev)
    }
  }

  moveRowDown(event) {
    const row = event.target.closest("tr")
    const next = row.nextElementSibling
    if (next) {
      this.tableBodyTarget.insertBefore(next, row)
    }
  }

  cellChanged(event) {
    const row = event.target.closest("tr")
    this.enforceContiguous(row)
  }

  enforceContiguous(row) {
    const inputs = row.querySelectorAll("td.segment-cell input")
    let foundEmpty = false
    inputs.forEach(input => {
      if (foundEmpty) {
        input.value = ""
        input.disabled = true
      } else {
        input.disabled = false
        if (input.value.trim() === "") {
          foundEmpty = true
        }
      }
    })
  }

  importPaste() {
    const text = this.pasteAreaTarget.value.trim()
    if (!text) return

    // Remove existing rows where segment 1 is blank
    this.tableBodyTarget.querySelectorAll("tr").forEach(row => {
      const firstInput = row.querySelector("td.segment-cell input")
      if (firstInput && firstInput.value.trim() === "") {
        row.remove()
      }
    })

    const lines = text.split("\n")
    lines.forEach(line => {
      const values = line.split("\t").map(v => v.trim()).filter(v => v !== "")
      if (values.length > 0) {
        this.addPathRow(values)
      }
    })
    this.pasteAreaTarget.value = ""
  }

  serialize() {
    const labels = this.getLabels()
    const delimiter = this.delimiterSelectTarget.value || "|"
    const paths = []

    this.tableBodyTarget.querySelectorAll("tr").forEach(row => {
      const inputs = row.querySelectorAll("td.segment-cell input")
      const path = []
      inputs.forEach(input => {
        const val = input.value.trim()
        if (val !== "") path.push(val)
      })
      if (path.length > 0) paths.push(path)
    })

    const segments = { labels, delimiter, paths }
    this.segmentsFieldTarget.value = JSON.stringify(segments)
  }

  getLabels() {
    return this.labelsInputTarget.value
      .split(",")
      .map(l => l.trim())
      .filter(l => l !== "")
  }
}
